import { useEffect, useRef, useState } from 'react';
import type { PreviewIframeMessage } from '@shared/utils/previewMessageBus';
import { previewMessageBusSubject, createPreviewMessageEventHandler } from '@shared/utils/previewMessageBus';
import { useTemplatePreviewStore } from '@apps/guest/routes/GuestSite/TemplatePreviewContext';
import { isInIframe } from '@shared/utils/isInIframe';
import globalWindow from '@shared/core/globals';
import type { WebsiteHeaderPresentationSettingsFragment } from '../useWebsiteHeaderSettings/useWebsiteHeaderSettings.graphql.generated';
import type { DesignLayoutType } from '@graphql/generated-base';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import equals from 'deep-equal';

type WebsiteHeaderUpdatesListenerOverrides = {
  activeHeaderPresentationLayout: Maybe<WebsiteHeaderPresentationSettingsFragment>;
  websiteLayout: Maybe<DesignLayoutType>;
  eventDisplayName: Maybe<string>;
  greetingMessage: Maybe<string>;
};

type UseWebsiteHeaderUpdatesListenerArgs = {
  initialValues: WebsiteHeaderUpdatesListenerOverrides;
};

const useWebsiteHeaderUpdatesListener = (args: UseWebsiteHeaderUpdatesListenerArgs) => {
  const { initialValues } = args;
  const isPreviewing = isInIframe();
  const templatePreview = useTemplatePreviewStore();

  // Check once whether a template preview is already active (e.g. after a layout switch
  // caused this component to remount). Used for both initial state and snapshot.
  const activePreviewOnMount = templatePreview.getActivePayload();

  const [overrides, setOverrides] = useState<WebsiteHeaderUpdatesListenerOverrides>(() => {
    if (activePreviewOnMount) {
      return {
        activeHeaderPresentationLayout: activePreviewOnMount.activeWebsiteHeaderPresentationLayout ?? initialValues.activeHeaderPresentationLayout,
        websiteLayout: activePreviewOnMount.eventDesign.websiteLayout?.layoutType ?? initialValues.websiteLayout,
        eventDisplayName: initialValues.eventDisplayName,
        greetingMessage: initialValues.greetingMessage,
      };
    }
    return {
      activeHeaderPresentationLayout: initialValues.activeHeaderPresentationLayout,
      websiteLayout: initialValues.websiteLayout,
      eventDisplayName: initialValues.eventDisplayName,
      greetingMessage: initialValues.greetingMessage,
    };
  });

  // If there's already an active template preview (remount scenario), snapshot the
  // GQL-based initial values so a future reset can restore them.
  const preTemplateOverridesRef = useRef<WebsiteHeaderUpdatesListenerOverrides | null>(
    activePreviewOnMount
      ? {
          activeHeaderPresentationLayout: initialValues.activeHeaderPresentationLayout,
          websiteLayout: initialValues.websiteLayout,
          eventDisplayName: initialValues.eventDisplayName,
          greetingMessage: initialValues.greetingMessage,
        }
      : null,
  );

  const hadSuppressedUpdatesRef = useRef(false);

  const processMessage = useEventCallback((message: PreviewIframeMessage) => {
    switch (message.action) {
      case 'fromTemplatePreviewPayload': {
        const payload = message.value;
        const updatedLayout = payload.activeWebsiteHeaderPresentationLayout;
        const updatedWebsiteLayout = payload.eventDesign.websiteLayout?.layoutType;

        if (!preTemplateOverridesRef.current) {
          preTemplateOverridesRef.current = { ...overrides };
        }
        hadSuppressedUpdatesRef.current = false;

        setOverrides(currentValue => ({
          ...currentValue,
          ...(updatedLayout !== undefined ? { activeHeaderPresentationLayout: updatedLayout } : {}),
          ...(updatedWebsiteLayout ? { websiteLayout: updatedWebsiteLayout } : {}),
        }));
        break;
      }
      case 'resetTemplatePreview': {
        const wasApplying = hadSuppressedUpdatesRef.current;
        hadSuppressedUpdatesRef.current = false;
        if (!wasApplying && preTemplateOverridesRef.current) {
          setOverrides(preTemplateOverridesRef.current);
        }
        preTemplateOverridesRef.current = null;
        break;
      }
      case 'fromWebsiteDesignerPayload': {
        // While a template preview is active, ignore intermediate design updates
        if (templatePreview.getActivePayload()) {
          hadSuppressedUpdatesRef.current = true;
          break;
        }
        preTemplateOverridesRef.current = null;
        const updatedPresentationLayout = message.value.eventDesign.activeWebsiteHeaderPresentationLayout;
        const updatedWebsiteLayout = message.value.eventDesign.websiteLayout.layoutType;
        if (!equals(updatedPresentationLayout, overrides.activeHeaderPresentationLayout) || updatedWebsiteLayout !== overrides.websiteLayout) {
          setOverrides(currentValue => ({
            ...currentValue,
            websiteLayout: updatedWebsiteLayout,
            activeHeaderPresentationLayout: updatedPresentationLayout,
          }));
        }
        break;
      }
      case 'fromList':
        message.value.forEach(messageValue => {
          switch (messageValue.key) {
            case 'eventDisplayName':
              setOverrides(currentValue => ({
                ...currentValue,
                eventDisplayName: messageValue.value,
              }));
              break;
            case 'greeting':
              setOverrides(currentValue => ({
                ...currentValue,
                greetingMessage: messageValue.value,
              }));
              break;
          }
        });
        break;
    }
  });

  useEffect(() => {
    if (!isPreviewing) {
      return;
    }

    const previewSub = previewMessageBusSubject.subscribe({
      next: action => {
        if (action.type === 'sendMessage') {
          processMessage(action.payload.message);
        }
      },
    });

    const receiveMessage = createPreviewMessageEventHandler(processMessage);
    globalWindow.addEventListener?.('message', receiveMessage, false);

    return () => {
      previewSub?.unsubscribe();
      globalWindow.removeEventListener?.('message', receiveMessage, false);
    };
  }, [isPreviewing, processMessage]);

  return { overrides };
};

export default useWebsiteHeaderUpdatesListener;
